import { Button, Col, Form, Input, Row, Steps } from "antd";
import React, { useEffect, useState } from "react";
import { DraggableModal } from "../../../../components/DraggableModal";
import { ProcessingCapacityItems } from "./ProcessingCapacityItems";
import { MapAddSingleMarker } from "../../../../components/maps/MapAddPoint";
import SelectOwnerShipTypes from "../../../../components/selects/SelectOwnerShipTypes";
import SelectTerritorialUnitsCode from "../../../../components/selects/SelectTerritorialUnitsCode";
import GeneralSelect from "../../../../components/selects/general/GeneralSelect";
import { EntProcessingCapacity, Profile } from "../../../../models/organic/processingCapacity";
import { EntBasicHB } from "../../../../models/handBooks/basicHB";
import { EntInitialFilter } from "../../../../models/filters/initialFilter";
import { useLazyGetRegisterStatusListQuery } from "../../../../redux/api/handBooksApi/organic/registerStatusApi";
import { GeoJsonPoint, Geometry } from "../../../../models/geoJsonPoint";
import _ from "lodash";
import { useTranslation } from "react-i18next";

const { Step } = Steps;

enum FormField {
  id,
  orgName,
  pin,
  ownershipTypeId,
  regionId,
  areaId,
  territorialUnitId,
  legalAddress,
  contactPerson,
  phoneNumbers,
  email,
  website,
  longitude,
  latitude,
  registerStatusId,
  profiles
}


interface IProps {
  show: boolean;
  item: EntProcessingCapacity;
  onHide: () => void;
  onSubmit: (pos: EntProcessingCapacity) => Promise<boolean>;
}

export const AddProcessingCapacityModal: React.FC<IProps> = (props) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  const [items, setItems] = useState<Profile[]>([]);
  const [geoJsonData, setGeoJsonData] = useState<any>(null);
  const [selectedRegionPolygonJson, setSelectedRegionPolygonJson] = useState<string|null>(null);

  const [selectedRegionId, setSelectedRegionId] = useState<number|null>(null);
  const [selectedRegionCode, setSelectedRegionCode] = useState<string|null>(null);
  const [selectedAreaCode, setSelectedAreaCode] = useState<string|null>(null);
  const [selectedAreaId, setSelectedAreaId] = useState<number|null>(null);

  const next = () => setCurrentStep((prev) => prev + 1);
  const prev = () => setCurrentStep((prev) => prev - 1);

  useEffect(() => {
	if (props.show) {
	  setFieldsFromEntity();
	  setCurrentStep(0);

	  if (props.item.id === null) {
		setItems([]);
	  } else if (props.item.profiles && props.item.profiles.length > 0) {
		setItems(props.item.profiles.map((item) => ({
		  ...item,
		  industryTypeId: item.industryTypeId || item.industryType?.id,
		  rawMaterialId: item.rawMaterialId || item.rawMaterial?.id,
		  productTypeId: item.productTypeId || item.productType?.id,
		  seasonalityId: item.seasonalityId || item.seasonality?.id,
		  certificateId: item.certificateId || item.certificate?.id,
		  destinationCountryId: item.destinationCountryId || item.destinationCitizenship?.id,
		})));
	  } else {
		setItems([]);
	  }

	  setSelectedRegionId(props.item.region?.id || null);
	  setSelectedAreaId(props.item.area?.id || null);
	  setSelectedAreaCode(props.item.territorialUnit?.code || null);
	}
  }, [props.show]);


  const setFieldsFromEntity = (): void => {
	form.setFields([
	  { name: FormField.orgName, value: props.item.orgName },
	  { name: FormField.pin, value: props.item.pin },
	  { name: FormField.ownershipTypeId, value: props.item.ownershipType?.id },
	  { name: FormField.regionId, value: props.item.region?.id },
	  { name: FormField.areaId, value: props.item.area?.id },
	  { name: FormField.territorialUnitId, value: props.item.territorialUnit?.id },
	  { name: FormField.legalAddress, value: props.item.legalAddress },
	  { name: FormField.contactPerson, value: props.item.contactPerson },
	  { name: FormField.phoneNumbers, value: props.item.phoneNumbers },
	  { name: FormField.email, value: props.item.email },
	  { name: FormField.website, value: props.item.website },
	  { name: FormField.longitude, value: props.item.territorialUnit?.id },
	  { name: FormField.latitude, value: props.item.territorialUnit?.id },
	  { name: FormField.registerStatusId, value: props.item.registerStatus?.id },
	]);
	if (props.item.profiles && props.item.profiles.length > 0) {
	  setItems(props.item.profiles.map((item) => ({
		...item,
		industryTypeId: item.industryTypeId || item.industryType?.id,
		rawMaterialId: item.rawMaterialId || item.rawMaterial?.id,
		productTypeId: item.productTypeId || item.productType?.id,
		seasonalityId: item.seasonalityId || item.seasonality?.id,
		certificateId: item.certificateId || item.certificate?.id,
		destinationCountryId: item.destinationCountryId || item.destinationCitizenship?.id,
	  })));
	}
  };

  const updateGeoJsonPoint = () => {
	const latitude = props.item.latitude;
	const longitude = props.item.longitude;

	if (latitude !== undefined && longitude !== undefined) {
	  const geoJsonPoint = new GeoJsonPoint();
	  geoJsonPoint.geometry = new Geometry();
	  geoJsonPoint.geometry.coordinates = [longitude, latitude];
	  setGeoJsonData(geoJsonPoint);
	} else {
	  console.error(t('breeding_farms.error_geometry'));
	}
  };

  useEffect(() => {
	if (props.item.latitude && props.item.longitude) {
	  updateGeoJsonPoint();
	} else {
	  setGeoJsonData(null);
	}
  }, [props.item.latitude, props.item.longitude]);

  const setEntityFromFields = (): EntProcessingCapacity => {
	let res = _.cloneDeep(props.item);
	res.orgName = form.getFieldValue(FormField.orgName);
	res.pin = form.getFieldValue(FormField.pin);
	res.ownershipTypeId = form.getFieldValue(FormField.ownershipTypeId);
	res.regionId = form.getFieldValue(FormField.regionId);
	res.areaId = form.getFieldValue(FormField.areaId);
	res.territorialUnitId = form.getFieldValue(FormField.territorialUnitId);
	res.legalAddress = form.getFieldValue(FormField.legalAddress);
	res.contactPerson = form.getFieldValue(FormField.contactPerson);
	const phones = form.getFieldValue(FormField.phoneNumbers);
	res.phoneNumbers = Array.isArray(phones)
	  ? phones
	  : phones
		? [phones]
		: [];
	res.email = form.getFieldValue(FormField.email);
	res.website = form.getFieldValue(FormField.website);
	if (geoJsonData !== null) {
	  res.latitude = geoJsonData.geometry?.coordinates[1] as number;
	  res.longitude = geoJsonData.geometry?.coordinates[0] as number;
	}
	res.registerStatusId = form.getFieldValue(FormField.registerStatusId);
	res.profiles = items;
	return res;
  }

  const handleSubmit = async () => {
	setLoading(true);
	try {
	  await form.validateFields();
	  const data = setEntityFromFields();
	  const result = await props.onSubmit(data);
	  if (result) props.onHide()
	} finally {
	  setLoading(false);
	}
  }

  const stepContent = [
	// Шаг 1
	<Row gutter={ 16 } key="step1">
	  <Col span={ 12 }>
		<Form.Item
		  label={ t('Наименование организации') }
		  name={ FormField.orgName }
		  rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
		>
		  <Input type="text"/>
		</Form.Item>
		<Form.Item
		  label={ t('ПИН организации') }
		  name={ FormField.pin }
		  rules={ [
			{ required: true, message: t("msg.fill_in_the_field") },
			{
			  validator: (_, value) => {
				if (value.length !== 14) {
				  return Promise.reject(t('msg.input_inn_err2'));
				}
				if (!/^\d{14}$/.test(value)) {
				  return Promise.reject(t('msg.input_inn_err3'));
				}
				const firstDigit = value[0];
				const day = parseInt(value.slice(1, 3), 10);
				const month = parseInt(value.slice(3, 5), 10);
				const year = parseInt(value.slice(5, 9), 10);
				const currentYear = new Date().getFullYear();

				if (!['0', '4'].includes(firstDigit)) {
				  return Promise.reject(t('msg.input_inn_err4'));
				}
				if (day < 1 || day > 31) {
				  return Promise.reject(t('msg.input_inn_err5'));
				}
				if (month < 1 || month > 12) {
				  return Promise.reject(t('msg.input_inn_err6'));
				}
				if (year < 1970 || year > currentYear) {
				  return Promise.reject(`${ t('msg.input_inn_err7') } ${ currentYear }`);
				}

				return Promise.resolve();
			  },
			},
		  ] }
		>
		  <Input
			maxLength={ 14 }
			inputMode="numeric"
			onChange={ (e) => {
			  const onlyDigits = e.target.value.replace(/\D/g, '');
			  e.target.value = onlyDigits;
			} }
			onKeyPress={ (e) => {
			  if (!/[0-9]/.test(e.key)) {
				e.preventDefault();
			  }
			} }
			onPaste={ (e) => {
			  const pasted = e.clipboardData.getData('text');
			  if (!/^\d+$/.test(pasted)) {
				e.preventDefault();
			  }
			} }
		  />
		</Form.Item>
		<Form.Item
		  label={ t('Форма собственности') }
		  name={ FormField.ownershipTypeId }
		  rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
		>
		  <SelectOwnerShipTypes/>
		</Form.Item>
		<Form.Item
		  label={ t('titles.territorial_unit_area') }
		  name={ FormField.regionId }
		  rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
		>
		  <SelectTerritorialUnitsCode
			parentId={ 1 }
			onChange={ (value, option: any) => {
			  setSelectedRegionId(Number(value));
			  setSelectedRegionPolygonJson(option?.data?.polygonJson);
			  setSelectedRegionCode(option?.data?.code);
			  setSelectedAreaCode(null);
			  setSelectedAreaId(null);
			  form.setFieldsValue({
				[FormField.regionId]: value,
				[FormField.areaId]: null, // Сбрасываем район
				[FormField.territorialUnitId]: null, // Сбрасываем населенный пункт
			  });
			} }
		  />
		</Form.Item>
		<Form.Item
		  label={ t('titles.territorialUnitDistricts') }
		  name={ FormField.areaId }
		  rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
		>
		  <SelectTerritorialUnitsCode
			type={ ['AREA'] }
			parentId={ selectedRegionId }
			onChange={ (value, option: any) => {
			  setSelectedRegionPolygonJson(option?.data?.polygonJson);
			  setSelectedAreaCode(option?.data?.code || null);
			  setSelectedAreaId(value as number);
			  form.setFields([{ name: FormField.territorialUnitId, value: null }]);
			} }
		  />
		</Form.Item>
		<Form.Item
		  label={ t("titles.territorialUnitVillage") }
		  name={ FormField.territorialUnitId }
		  rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
		>
		  <SelectTerritorialUnitsCode areaCode={ selectedAreaCode }/>
		</Form.Item>
	  </Col>
	  <Col span={ 12 }>
		<Form.Item
		  label={ t('Юридический адрес') }
		  name={ FormField.legalAddress }
		  rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
		>
		  <Input type="text"/>
		</Form.Item>
		<Form.Item
		  label={ t('Контактное лицо') }
		  name={ FormField.contactPerson }
		  rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
		>
		  <Input type="text"/>
		</Form.Item>
		<Form.Item label={ t('Номера телефонов') }>
		  <Form.List name={ FormField.phoneNumbers }>
			{ (fields, { add, remove }) => (
			  <>
				{ fields.map((field) => (
				  <div key={ field.key } style={ { display: 'flex', marginBottom: 8, gap: '8px' } }>
					<Form.Item
					  { ...field }
					  style={ { flex: 1 } }
					  rules={ [
						{ required: true, message: t('msg.fill_in_the_field') },
						{
						  pattern: /^\+?\d{9,15}$/,
						  message: t('msg.input_phone_err'),
						},
					  ] }
					>
					  <Input placeholder={ t('Введите номер телефона') }/>
					</Form.Item>
					<Button
					  danger
					  type="text"
					  onClick={ () => remove(field.name) }
					>
					  { t('Удалить') }
					</Button>
				  </div>
				)) }
				<Button
				  type="dashed"
				  onClick={ () => add() }
				  block
				>
				  + { t('Добавить номер') }
				</Button>
			  </>
			) }
		  </Form.List>
		</Form.Item>

		<Form.Item
		  label={ t('Электронная почта') }
		  name={ FormField.email }
		  rules={ [
			{ type: 'email', message: 'Введите корректный e-mail' },
		  ] }
		>
		  <Input type="mail"/>
		</Form.Item>
		<Form.Item
		  label={ t('Веб-сайт') }
		  name={ FormField.website }
		>
		  <Input type="text"/>
		</Form.Item>
		<Form.Item
		  label={ t('Статус реестра') }
		  name={ FormField.registerStatusId }
		  rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
		>
		  <GeneralSelect<EntBasicHB, EntInitialFilter>
			queryHook={ useLazyGetRegisterStatusListQuery }
			initialFilter={ new EntInitialFilter() }
		  />
		</Form.Item>
	  </Col>
	  <Col span={ 24 }>
		<MapAddSingleMarker
		  polygonJson={ selectedRegionPolygonJson }
		  setGeoJsonData={ setGeoJsonData }
		  geoJsonData={ geoJsonData }
		/>
	  </Col>
	</Row>,

	// Шаг 2
	<ProcessingCapacityItems key="step2" items={ items } setItems={ setItems }/>,

	// Шаг 3
	<div key="step3">
	  <Form.Item label="Документы">
		<Input.TextArea rows={ 4 } placeholder="Здесь можно добавить данные по документам..."/>
	  </Form.Item>
	</div>,
  ];

  return (
	<DraggableModal
	  title={ props.item.id ? "Редактировать мощность" : "Добавить мощность" }
	  visible={ props.show }
	  onCancel={ props.onHide }
	  noFooter
	  width={ 700 }
	>
	  <Steps
		current={ currentStep }
		onChange={ (step) => setCurrentStep(step) }
		style={ { marginBottom: 24 } }
	  >
		<Step title="Основной реестр"/>
		<Step title="Профиль переработки"/>
		<Step title="Документы"/>
	  </Steps>

	  <Form form={ form } layout="vertical">
		{ stepContent[currentStep] }

		<div style={ { marginTop: 24, textAlign: "right" } }>
		  <Button onClick={ props.onHide } style={ { marginRight: 8 } }>
			Отмена
		  </Button>

		  {/*{ currentStep > 0 && (*/ }
		  {/*<Button style={ { marginRight: 8 ,textAlign: "left" } } onClick={ prev }>*/ }
		  {/*  Назад*/ }
		  {/*</Button>*/ }
		  {/*) }*/ }

		  { currentStep < stepContent.length - 1 && (
			<Button
			  type="primary"
			  style={ { backgroundColor: "#00A65A", borderColor: "#00A65A" } }
			  onClick={ next }
			>
			  Продолжить
			</Button>
		  ) }

		  { currentStep === stepContent.length - 1 && (
			<Button
			  type="primary"
			  style={ { backgroundColor: "#00A65A", borderColor: "#00A65A" } }
			  loading={ loading }
			  onClick={ handleSubmit }
			>
			  Сохранить
			</Button>
		  ) }
		</div>
	  </Form>
	</DraggableModal>
  );
};
